import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaTrashAlt } from "react-icons/fa";

// NewMachinePhotos – lokální (před prvním uložením) správce fotek pro vytváření stroje.
// Drží vybrané soubory v paměti, umožňuje změnu pořadí (drag & drop + šipky) a mazání
// jednotlivých fotek ještě před uložením. První fotka v pořadí je náhledová. Při každé
// změně předá rodiči seřazené pole souborů přes onChange; rodič je nahraje při uložení.
let uidCounter = 0;

export const NewMachinePhotos = ({ onChange }) => {
  const [items, setItems] = useState([]); // { uid, file, url }
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  // Rodič dostane vždy aktuální seřazené soubory.
  useEffect(() => {
    onChange(items.map((it) => it.file));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Úklid object URL při odchodu ze stránky.
  useEffect(() => {
    return () => items.forEach((it) => URL.revokeObjectURL(it.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const added = Array.from(fileList).map((file) => ({ uid: ++uidCounter, file, url: URL.createObjectURL(file) }));
    setItems((prev) => [...prev, ...added]);
  };

  const removeAt = (index) => {
    setItems((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.url);
      return next;
    });
  };

  const move = (index, dir) => {
    const target = index + dir;
    setItems((prev) => {
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const onDrop = (dropIndex) => {
    setOverIndex(null);
    setItems((prev) => {
      if (dragIndex === null || dragIndex === dropIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  return (
    <div style={{ marginTop: "1.5rem" }}>
      <h1 style={{ marginBottom: "0.25rem" }}>Fotky stroje</h1>
      <p style={{ color: "#666", fontSize: "13px", marginTop: 0 }}>
        Přetažením (nebo šipkami) změníte pořadí, ikonou koše smažete jednotlivou fotku. První fotka je náhledová. Fotky se nahrají po uložení stroje.
      </p>

      {items.length === 0 ? (
        <p style={{ color: "#999" }}>Zatím žádné fotky – přidejte alespoň jednu.</p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
          {items.map((it, index) => (
            <div
              key={it.uid}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => {
                e.preventDefault();
                if (overIndex !== index) setOverIndex(index);
              }}
              onDrop={() => onDrop(index)}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              style={{
                position: "relative",
                width: "140px",
                height: "140px",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "grab",
                border: overIndex === index ? "2px solid #E00B0B" : "2px solid transparent",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                background: `#f2f2f2 url(${it.url}) center/cover no-repeat`,
              }}
              title="Přetáhněte pro změnu pořadí"
            >
              {index === 0 && (
                <span style={{ position: "absolute", top: 6, left: 6, background: "#262626", color: "#fff", fontSize: "11px", padding: "2px 6px", borderRadius: "4px" }}>Náhled</span>
              )}
              <button
                type="button"
                onClick={() => removeAt(index)}
                style={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(224,11,11,0.9)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                title="Smazat fotku"
              >
                <FaTrashAlt size={13} />
              </button>

              {/* Šipky pro změnu pořadí – fungují i na dotyku (drag & drop na desktopu) */}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "flex", justifyContent: "space-between", background: "rgba(0,0,0,0.45)" }}>
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "transparent",
                    color: index === 0 ? "rgba(255,255,255,0.3)" : "#fff",
                    padding: "7px 0",
                    cursor: index === 0 ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Posunout doleva"
                >
                  <FaChevronLeft size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "transparent",
                    color: index === items.length - 1 ? "rgba(255,255,255,0.3)" : "#fff",
                    padding: "7px 0",
                    cursor: index === items.length - 1 ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Posunout doprava"
                >
                  <FaChevronRight size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: "1rem" }}>
        <label style={{ display: "inline-block", background: "#262626", color: "#fff", padding: "10px 16px", borderRadius: "6px", cursor: "pointer" }}>
          + Přidat fotky
          <input
            type="file"
            accept="image/jpeg"
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = ""; // umožní znovu vybrat stejný soubor
            }}
            style={{ display: "none" }}
          />
        </label>
      </div>
    </div>
  );
};

export default NewMachinePhotos;
